import { AxiosError } from 'axios';
import { blobToJson } from 'shell/utils/base64-to-blob';
import { ReactElement, ReactNode, ReactText, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { __BACKEND_URL__ } from 'shell/constants/environment';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';
import { useErrorHandler } from 'shell/error/hooks/use-error-handler';
import { RequestOptions, Response } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpLoading } from 'shell/hooks/use-http-loading';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { HttpResponse } from 'shell/models/http-response';
import { HttpStatusCode } from 'shell/types/http-status-code';
import { HttpProvider as HttpProviderCustom } from './http-provider';
import { HttpProvider as HttpProviderLib } from './http-provider-library';

interface HttpProviderProps {
  children: ReactNode;
}

export const HttpProvider = ({ children }: HttpProviderProps): ReactElement => {
  const httpLoading = useHttpLoading();
  const { setError } = useErrorHandler();
  const { setUserProfile } = useUserProfile();

  const toastAuthentication = useRef<ReactText>('');

  const httpProviderOpts = useMemo(
    () => ({
      request: {
        onFulfilled: (config: RequestOptions) => {
          if (config.loading) {
            httpLoading.addLoading();
          }

          return config;
        },
        onRejected: (error: AxiosError) => {
          httpLoading.removeLoading();

          return Promise.reject(error);
        },
      },
      response: {
        onFulfilled: <T,>(response: Response<HttpResponse<T> | Blob>) => {
          if (response?.config?.loading) {
            httpLoading.removeLoading();
          }

          if (
            response?.config?.errorCatching &&
            response?.config?.responseType === 'blob' &&
            (response?.data as Blob)?.type === 'application/json'
          ) {
            blobToJson(response.data as Blob).then((json) => {
              if ((json as unknown as HttpResponse<T>)?.summary?.error) {
                const { code, messages } = (json as unknown as HttpResponse<T>)?.summary || {};
                const backError = new HttpBackendError(
                  Number.parseInt(code, 10),
                  messages,
                  HttpStatusCode.Ok,
                  true
                );
                setError(backError);
                return Promise.reject();
              }
            });
            return Promise.reject();
          }

          if (
            (response?.data as HttpResponse<T>)?.summary?.error &&
            response?.config?.errorCatching
          ) {
            const { code, messages } = (response?.data as HttpResponse<T>)?.summary || {};

            const backError = new HttpBackendError(
              Number.parseInt(code, 10),
              messages,
              HttpStatusCode.Ok,
              true
            );

            setError(backError);

            return Promise.reject();
          }
          return response;
        },

        onRejected: (error: AxiosError) => {
          // Do something with response error
          const statusCode = Number(error?.response?.status);

          const messages = [error.message];

          const backendError = new HttpBackendError(statusCode, messages, statusCode, true);

          if (error?.response?.status) {
            if (backendError.statusCode === HttpStatusCode.Unauthorized) {
              setUserProfile();

              if (!toast.isActive(toastAuthentication.current)) {
                toastAuthentication.current = toast.error(
                  'Non autorizzato, si prega di ripetere il login'
                );
              }
            } else {
              setError(backendError as HttpBackendError);
            }

            httpLoading.removeLoading();
          }

          return Promise.reject(error);
        },
      },
    }),
    [setUserProfile, httpLoading, setError]
  );

  return (
    <HttpProviderCustom
      requestInterceptors={httpProviderOpts.request}
      responseInterceptors={httpProviderOpts.response}
      baseUrl={__BACKEND_URL__}
    >
      <HttpProviderLib>{children}</HttpProviderLib>
    </HttpProviderCustom>
  );
};

HttpProvider.displayName = 'HttpProvider';
